/** Xodim profili (backend: apps/dashboards/people.py). Ko'rinish va maosh ruxsati backend'da. */
import { useEffect } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type RequestOptions } from "@/lib/api";
import { subscribeEvents } from "@/lib/realtime";
import type { AdminUser } from "@/features/admin/types";
import type { Bonus, Salary } from "@/features/budget/types";
import type { Role } from "@/features/auth/types";
import type { FunnelStep, RetentionRow } from "@/features/dashboards/types";
import type { DriverStatus } from "@/features/drivers/types";

export interface PersonUser {
  id: number;
  full_name: string;
  display_name: string;
  initials: string;
  username: string;
  phone: string;
  role: Exclude<Role, "carrier">;
  branch: { id: number; name: string } | null;
  /** Rahbar: filial menejeri (o'zidan tashqari) */
  manager: { id: number; display_name: string } | null;
  timezone: string;
  is_blocked: boolean;
  blocked_at: string | null;
  totp_enabled: boolean;
  created_at: string;
  last_login: string | null;
  drivers: { active: number; hired: number; lost: number; total: number };
}

export interface PersonKpis {
  new_drivers: number;
  new_drivers_prev: number;
  submitted: number;
  waiting_reply: number;
  hires: number;
  hires_prev: number;
  hires_pending: number;
  in_pipeline: number;
  avg_time_to_hire_days: number | null;
  avg_time_to_hire_prev: number | null;
  retention_30: number | null;
  calls: number;
  activities: number;
  overdue_tasks: number;
}

export interface PersonMonth {
  period: string;
  hires: number;
  submitted: number;
  new: number;
  calls: number;
  salary?: string | null;
  bonus?: string | null;
}

export interface PersonActivity {
  id: number;
  type: "call" | "note" | "sms" | "email" | "status";
  body: string;
  outcome: string;
  duration_sec: number | null;
  created_at: string;
  driver: { id: number; full_name: string };
}

export interface PersonPay {
  salaries: { year: number; month: number; amount: string; currency: string; paid: boolean; paid_at: string | null; note: string }[];
  bonuses: { year: number; month: number; part: number; hires: number; retained_30: number; total: string; currency: string; status: "draft" | "approved" | "paid"; paid_at: string | null }[];
  estimate: { hires: number; retained_30: number; amount: number; currency: string; rule: string; status: string } | null;
  /** Tanlangan oy (versiya bilan, tahrirlash uchun); suggested -- o'tgan oy maoshi */
  month: { salary: Salary | null; suggested: string | null; bonus: Bonus | null };
}

export interface PersonProfile {
  user: PersonUser;
  /** can_edit_pay -- maosh va bonusni shu yerdan boshqarish (Budget bilan bir xil qoida) */
  permissions: { is_self: boolean; can_see_pay: boolean; can_manage: boolean; can_edit_pay: boolean };
  admin_user: AdminUser | null;
  period: { value: string; label: string; start: string; end: string };
  kpis: PersonKpis;
  funnel: FunnelStep[];
  pipeline: Partial<Record<DriverStatus, number>>;
  retention: RetentionRow[];
  monthly: PersonMonth[];
  rank: { position: number | null; of: number; branch: string } | null;
  recent_activity: PersonActivity[];
  pay: PersonPay | null;
}

export const personKeys = {
  // "user" -- WebSocket'dagi user hodisasi (admin o'zgartirsa) shu kalitni yangilaydi
  profile: (id: number | string, period: string) => ["user", "profile", String(id), period] as const,
};

export function usePersonProfile(id: number | string | undefined, period: string) {
  const qc = useQueryClient();
  useEffect(() => {
    // Driver, faollik, maosh yoki bonus o'zgarsa raqamlar eskiradi; ketma-ket hodisalar bitta so'rovga yig'iladi (dashboard kabi)
    let timer: number | undefined;
    const off = subscribeEvents(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => qc.invalidateQueries({ queryKey: ["user", "profile"] }), 1500);
    });
    return () => {
      window.clearTimeout(timer);
      off();
    };
  }, [qc]);
  return useQuery({
    queryKey: personKeys.profile(id ?? "", period),
    queryFn: () => api.get<PersonProfile>(`/api/people/${id}/`, { query: { period } }),
    enabled: !!id,
  });
}


// ---- Xodimlar ro'yxati (People sahifasi, backend: people.people_list) ----

export interface PersonRow {
  id: number;
  full_name: string;
  username: string;
  display_name: string;
  initials: string;
  role: Exclude<Role, "carrier">;
  branch: { id: number; name: string } | null;
  is_blocked: boolean;
  last_login: string | null;
  drivers_active: number;
  new: number;
  submitted: number;
  hired: number;
  conversion: number | null;
  calls: number;
  last_activity_at: string | null;
  overdue_tasks: number;
}

export interface PeopleList {
  period: { value: string; label: string };
  results: PersonRow[];
}

export function usePeople(query: RequestOptions["query"]) {
  const qc = useQueryClient();
  useEffect(() => {
    let timer: number | undefined;
    const off = subscribeEvents(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => qc.invalidateQueries({ queryKey: ["user", "people"] }), 1500);
    });
    return () => {
      window.clearTimeout(timer);
      off();
    };
  }, [qc]);
  return useQuery({
    queryKey: ["user", "people", query],
    queryFn: () => api.get<PeopleList>("/api/people/", { query }),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}
